import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import client from '../api/client';
import { useDesign } from '../contexts/DesignContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import LanguagePickerLarge from '../components/LanguagePickerLarge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Dumbbell, Mail, Lock, Loader2, Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown,
  Eye, EyeOff
} from 'lucide-react';
import { getDisplayName } from '../lib/displayName';

const LOGO_ICONS = { Dumbbell, Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown };

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [cancelDeletionLoading, setCancelDeletionLoading] = useState(false);
  const { login } = useAuth();
  const { design } = useDesign();
  const navigate = useNavigate();

  const pc = design.primary_color || '#FF5C00';
  const LogoIcon = LOGO_ICONS[design.logo_icon] || Dumbbell;
  const appLeft = design.app_name_left || 'trainedby';
  const appRight = design.app_name_right || 'RS';
  const hf = design.heading_font || 'Barlow Condensed';

  const handleOAuthLogin = async (provider) => {
    try {
      const redirectUri = `${window.location.origin}/auth/callback/${provider}`;
      const response = await client.get(`/auth/oauth/url?provider=${provider}&redirect_uri=${encodeURIComponent(redirectUri)}&intent=login`);
      window.location.href = response.data.url;
    } catch (err) {
      toast.error(t('auth.oauthFailed', 'Failed to start login'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`${t('dashboard.welcome')}, ${getDisplayName(user.name)}!`);
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (
        error.response?.status === 403 &&
        typeof detail === 'object' &&
        detail !== null &&
        detail.code === 'account_scheduled_for_deletion'
      ) {
        setPendingDeletion({
          cancelToken: detail.cancel_token,
          purgeAt: detail.deletion_purge_at,
          email,
          password,
        });
        return;
      }
      toast.error(
        (typeof detail === 'string' ? detail : detail?.message) ||
          t('errors.loginFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletionAndLogin = async () => {
    if (!pendingDeletion) return;
    setCancelDeletionLoading(true);
    try {
      await client.post('/profile/cancel-deletion', null, {
        headers: { Authorization: `Bearer ${pendingDeletion.cancelToken}` },
      });
      toast.success(t('login.deletionCancelled', 'Deletion cancelled. Logging in...'));
      const user = await login(
        pendingDeletion.email,
        pendingDeletion.password
      );
      setPendingDeletion(null);
      toast.success(`${t('dashboard.welcome')}, ${getDisplayName(user.name)}!`);
      navigate('/dashboard');
    } catch (e) {
      toast.error(
        e.response?.data?.detail?.message ||
          (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : null) ||
          t('login.deletionCancelError', 'Could not cancel deletion')
      );
    } finally {
      setCancelDeletionLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex" data-testid="login-page">
      {/* Left side - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src={design.login_hero_image || "https://images.unsplash.com/photo-1639660299469-9e9279364c97?w=1200&q=80"}
          alt={t('login.heroAlt')}
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
        <div className="absolute bottom-8 left-10 right-10">
          <h1 className="text-4xl xl:text-5xl font-bold text-white tracking-tight mb-2" style={{ fontFamily: `'${hf}', sans-serif` }}>
            {design.login_tagline_top || 'TRAIN SMARTER.'}<br />
            <span style={{ color: pc }}>{design.login_tagline_bottom || 'ACHIEVE MORE.'}</span>
          </h1>
          <p className="text-neutral-300 text-base xl:text-lg">
            {design.login_subtitle || 'The complete platform for coaches and athletes to track workouts, nutrition, and progress.'}
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex flex-col bg-background h-full">
        {/* The old 32x20px greyscale flag row lived here. It was below the 48px
            touch-target minimum and easy to miss, so it was replaced by the
            prominent LanguagePickerLarge above the form. */}

        {/* Centered form area */}
        <div className="flex-1 flex items-center justify-center px-6 pb-6">
          <div className="w-full max-w-md space-y-5">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-2.5 rounded-xl" style={{ backgroundColor: pc, boxShadow: `0 0 20px ${pc}26` }}>
                  <LogoIcon className="h-7 w-7 text-white" />
                </div>
                <span className="text-2xl xl:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {appLeft}<span style={{ color: pc }}>{appRight}</span>
                </span>
              </div>
            </div>

            <LanguagePickerLarge className="mb-4" />

            <Card className="border-border/50 shadow-xl">
              <CardHeader className="space-y-1 pb-3 pt-5 px-5">
                <CardTitle className="text-xl xl:text-2xl font-bold tracking-tight" style={{ fontFamily: `'${hf}', sans-serif` }}>
                  {t('auth.signIn').toUpperCase()}
                </CardTitle>
                <CardDescription className="text-sm">
                  {t('auth.loginSubtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm font-medium">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="coach@trainedbyrs.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-sm font-medium">{t('auth.password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                        data-testid="login-toggle-password"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-end pt-1">
                    <Link to="/forgot-password" className="text-sm hover:underline" style={{ color: pc }} data-testid="forgot-password-link">
                      {t('auth.forgotPassword', 'Forgot password?')}
                    </Link>
                  </div>
                  <Button
                    type="submit"
                    className="w-full text-white font-bold uppercase tracking-wider transition-all"
                    style={{ backgroundColor: pc, boxShadow: `0 0 15px ${pc}4D` }}
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('auth.signingIn')}
                      </>
                    ) : (
                      t('auth.signIn')
                    )}
                  </Button>
                </form>

                {/* OAuth Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      {t('auth.orContinueWith', 'or continue with')}
                    </span>
                  </div>
                </div>

                {/* OAuth Buttons */}
                <div className="space-y-2.5">
                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('facebook')}
                    className="w-full flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-md text-white font-medium text-sm transition-opacity hover:opacity-90"
                    style={{ backgroundColor: '#1877F2' }}
                    data-testid="login-facebook-btn"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    {t('auth.continueWithFacebook', 'Continue with Facebook')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('instagram')}
                    className="w-full flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-md text-white font-medium text-sm transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
                    data-testid="login-instagram-btn"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C16.67.014 16.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                    </svg>
                    {t('auth.continueWithInstagram', 'Continue with Instagram')}
                  </button>
                </div>

                <div className="mt-4 text-center text-sm">
                  <span className="text-muted-foreground">{t('auth.noAccount')} </span>
                  <Link to="/register" className="font-medium" style={{ color: pc }} data-testid="register-link">
                    {t('auth.signUp')}
                  </Link>
                </div>
                <div className="mt-3 flex items-center justify-center gap-3 text-xs text-muted-foreground">
                  <Link to="/privacy-policy" className="hover:underline" data-testid="login-privacy-link">
                    {t('login.privacyPolicy')}
                  </Link>
                  <span>|</span>
                  <Link to="/terms-of-service" className="hover:underline" data-testid="login-terms-link">
                    {t('login.termsOfService')}
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {pendingDeletion && (
        <Dialog
          open={true}
          onOpenChange={(open) => {
            if (!open && !cancelDeletionLoading) setPendingDeletion(null);
          }}
        >
          <DialogContent data-testid="account-deletion-pending-modal">
            <DialogHeader>
              <DialogTitle>
                {t('login.deletionPendingTitle', 'Account scheduled for deletion')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <p>
                {t(
                  'login.deletionPendingBody',
                  'This account will be permanently deleted on {{date}}.',
                  {
                    date: pendingDeletion.purgeAt
                      ? new Date(pendingDeletion.purgeAt).toLocaleDateString()
                      : t('login.deletionPendingFutureDate', 'a future date'),
                  }
                )}
              </p>
              <p>
                {t(
                  'login.deletionPendingPrompt',
                  'Cancel the deletion to continue using your account.'
                )}
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPendingDeletion(null)}
                disabled={cancelDeletionLoading}
                data-testid="keep-deletion-btn"
              >
                {t('login.keepDeletion', 'Keep deletion scheduled')}
              </Button>
              <Button
                onClick={handleCancelDeletionAndLogin}
                disabled={cancelDeletionLoading}
                style={{ backgroundColor: pc, color: 'white' }}
                data-testid="cancel-deletion-and-login-btn"
              >
                {cancelDeletionLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.signingIn')}
                  </>
                ) : (
                  t('login.cancelDeletionAndLogin', 'Cancel deletion & log in')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
